import { NearbyProject, useNearbyProjects } from "@/hooks/use-nearby-projects";
import { bengaluruBounds } from "@/info/bengaluru-gps";
import { mapDefaults } from "@/info/map-defaults";
import { isWithinBounds } from "@/utils/location-utils";
import { logger } from "@/utils/logger";
import { MapCameraChangedEvent, MapEvent, MapMouseEvent, useMap } from "@vis.gl/react-google-maps";
import { deepEqual as isEqualFunc } from "fast-equals";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Path, useForm, useWatch } from "react-hook-form";
import { toast } from "sonner";
import { PlaceDetails } from "../../others/place-autocomplete/types";
import { useFormContext } from "../context/FormProvider";
import { checkLocation, geocodeLocation } from "../map/utils/coords";
import { isProjectName, parseAddressComponents } from "../utils/address-components.util";
export interface AddressFormData {
  projectName: string;
  projectId?: string;
  flatNumber?: string;
  blockName?: string;
  location: {
    locality: string;
    address: string;
    city: string;
    pin: string;
    coordinates: {
      lat: number;
      lng: number;
    };
  };
}
interface ProfileFormData {
  propertyType: {
    type: string;
    subType: string;
    houseType?: string;
  };
  isCommunity?: boolean;
}
interface UseAddressMapProps {
  onNext: (data: AddressFormData) => void;
  isDraggable?: boolean;
}

// Move outside hook - computed once
const convertBounds = (bounds: google.maps.LatLngBounds | {
  north: number;
  south: number;
  east: number;
  west: number;
}) => {
  if ("north" in bounds && "south" in bounds && "east" in bounds && "west" in bounds) {
    return bounds;
  }
  const latLngBounds = bounds as google.maps.LatLngBounds;
  const ne = latLngBounds.getNorthEast();
  const sw = latLngBounds.getSouthWest();
  return {
    north: ne.lat(),
    south: sw.lat(),
    east: ne.lng(),
    west: sw.lng()
  };
};
export function useAddressMap({
  onNext
}: UseAddressMapProps) {
  const map = useMap();
  const [mapBounds, setMapBounds] = useState<ReturnType<typeof convertBounds> | undefined>(undefined);

  // Memoize bounds update to prevent recreation
  const updateBounds = useCallback(() => {
    if (!map) return;
    const bounds = map.getBounds();
    if (bounds) {
      setMapBounds(convertBounds(bounds));
    }
  }, [map]);

  // Update mapBounds whenever the map bounds change
  useEffect(() => {
    if (!map) return;

    // Initial bounds
    updateBounds();

    // Listen for bounds changes
    const listener = map.addListener("bounds_changed", updateBounds);
    return () => {
      google.maps.event.removeListener(listener);
    };
  }, [map, updateBounds]);
  const {
    state,
    updateStepData
  } = useFormContext();
  const contextAddress = state.address as Partial<AddressFormData>;
  const contextProfile = state.profile as Partial<ProfileFormData>;
  const [mapCenter, setMapCenter] = useState(contextAddress?.location?.coordinates || mapDefaults.center);
  const [mapZoom, setMapZoom] = useState(mapDefaults.zoom);
  const [initialBounds, setInitialBounds] = useState<ReturnType<typeof convertBounds> | undefined>(undefined);
  const isProjectSupported = useMemo(() => state.profile?.propertyType?.type === "project", [state.profile?.propertyType?.type]);

  // Memoize initial disabled state
  const initialDisabledState = useMemo(() => ({
    projectName: Boolean(isProjectSupported && contextAddress.projectName),
    projectId: Boolean(isProjectSupported && contextAddress.projectId),
    "location.locality": Boolean(contextAddress?.location?.coordinates),
    "location.pin": Boolean(contextAddress?.location?.pin)
  }), [isProjectSupported, contextAddress.projectName, contextAddress.projectId, contextAddress?.location?.coordinates, contextAddress?.location?.pin]);
  const [isDisabled, setIsDisabled] = useState<Partial<Record<string, boolean>>>(initialDisabledState);
  const {
    data: nearbyProjects,
    onRefetch
  } = useNearbyProjects({
    bounds: mapBounds ?? undefined,
    maxDistance: 500
  });

  // State
  const [showForm, setShowForm] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState<string | null>(null);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [centerActivated, setCenterActivated] = useState(false);
  const [justClicked, setJustClicked] = useState(false);
  const [addressData, setAddressData] = useState<string>(contextAddress?.location?.address || "");
  const [hasUserSelectedLocation, setHasUserSelectedLocation] = useState<boolean>(Boolean(contextAddress?.location?.coordinates));
  const [isHovered, setIsHovered] = useState<number | null>(null);

  // Refs
  const markerFetchRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const markerCoordRef = useRef<{
    lat: number;
    lng: number;
  } | null>(null);
  const justClickedTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const defaultValues = useMemo(() => ({
    projectName: isProjectSupported ? contextAddress?.projectName || "" : "",
    projectId: isProjectSupported ? contextAddress?.projectId || "" : "",
    flatNumber: contextAddress?.flatNumber || "",
    blockName: contextAddress?.blockName || "",
    location: {
      locality: contextAddress?.location?.locality || "",
      address: contextAddress?.location?.address || "",
      city: contextAddress?.location?.city || "bengaluru",
      pin: contextAddress?.location?.pin || "",
      coordinates: contextAddress?.location?.coordinates || {
        lat: mapDefaults.center.lat,
        lng: mapDefaults.center.lng
      }
    }
  }), [contextAddress, isProjectSupported]);
  const {
    control,
    handleSubmit,
    setValue,
    reset
  } = useForm<AddressFormData>({
    defaultValues
  });

  // Reset if defaults change
  const prevDefaults = useRef(defaultValues);
  useEffect(() => {
    if (!isEqualFunc(prevDefaults.current, defaultValues)) {
      reset(defaultValues);
      prevDefaults.current = defaultValues;
    }
  }, [defaultValues, reset]);

  // Memoize property type and isCommunity
  const propertyType = useMemo(() => contextProfile?.propertyType || {
    type: "",
    subType: "",
    houseType: ""
  }, [contextProfile?.propertyType]);
  const isCommunity = useMemo(() => contextProfile?.isCommunity || false, [contextProfile?.isCommunity]);

  // Memoize watched values - more stable dependencies
  const watchedValues = useWatch({
    control
  });

  // Create a stable memoized version
  const memoizedWatchedValues = useMemo(() => {
    const coords = watchedValues?.location?.coordinates;
    return {
      projectName: watchedValues?.projectName || "",
      projectId: watchedValues?.projectId || "",
      flatNumber: watchedValues?.flatNumber || "",
      blockName: watchedValues?.blockName || "",
      location: {
        locality: watchedValues?.location?.locality || "",
        address: watchedValues?.location?.address || "",
        city: watchedValues?.location?.city || "",
        pin: watchedValues?.location?.pin || "",
        coordinates: coords ? {
          lat: coords.lat,
          lng: coords.lng
        } : {
          lat: 0,
          lng: 0
        }
      }
    };
  }, [watchedValues?.blockName, watchedValues?.flatNumber, watchedValues?.location?.address, watchedValues?.location?.city, watchedValues?.location?.coordinates, watchedValues?.location?.locality, watchedValues?.location?.pin, watchedValues?.projectId, watchedValues?.projectName]);
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      updateStepData("address", memoizedWatchedValues);
    }, 3000);
    return () => clearTimeout(timeoutId);
  }, [memoizedWatchedValues, updateStepData]);

  // Cleanup justClicked timeout on unmount
  const scheduleJustClickedReset = useCallback(() => {
    if (justClickedTimeoutRef.current) clearTimeout(justClickedTimeoutRef.current);
    justClickedTimeoutRef.current = setTimeout(() => setJustClicked(false), 100);
  }, []);
  useEffect(() => {
    return () => {
      if (justClickedTimeoutRef.current) clearTimeout(justClickedTimeoutRef.current);
    };
  }, []);

  // Track mounted state to prevent updates after unmount
  const isMountedRef = useRef(true);
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Initial restore
  const initialMount = useRef(true);
  useEffect(() => {
    if (initialMount.current) {
      if (contextAddress?.location?.coordinates) {
        setMapCenter(contextAddress.location.coordinates);
        setHasUserSelectedLocation(true);
      }
      if (contextAddress?.location?.address) {
        setValue("location.address", contextAddress.location.address, {
          shouldDirty: true
        });
      }
      initialMount.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const updateFormFields = useCallback((fielddata: Record<string, unknown>) => {
    Object.entries(fielddata).forEach(([key, value]) => {
      return setValue(key as Path<AddressFormData>, value as any, {
        shouldDirty: true
      });
    });
  }, [setValue]);

  // Refetch markers on map center change - optimized
  useEffect(() => {
    if (!map || !mapCenter || !mapBounds) return;
    const currentBounds = mapBounds;

    // Initialize bounds on first load and fetch immediately
    if (!initialBounds) {
      setInitialBounds(currentBounds);
      onRefetch();
      return;
    }

    // Clear existing timeout
    if (markerFetchRef.current) {
      clearTimeout(markerFetchRef.current);
    }

    // Debounce the bounds check for subsequent movements
    markerFetchRef.current = setTimeout(() => {
      const currentPosition = {
        latitude: mapCenter.lat,
        longitude: mapCenter.lng
      };

      // Only refetch if OUTSIDE initial bounds
      if (!isWithinBounds(currentPosition, initialBounds) && isWithinBounds(currentPosition, bengaluruBounds)) {
        onRefetch();
        setInitialBounds(currentBounds);
      }
      markerCoordRef.current = mapCenter;
    }, 1000);
    return () => {
      if (markerFetchRef.current) {
        clearTimeout(markerFetchRef.current);
      }
    };
  }, [map, mapCenter.lat, mapCenter.lng, initialBounds, mapBounds, onRefetch, mapCenter]);

  // Memoize toast handler
  const handleOutOfBoundsToast = useCallback(() => {
    toast.error("Location Out of Serviceable Bounds", {
      description: "Please select a location within our service area."
    });
    setShowForm(false);
  }, []);

  // Memoize place select handler
  const handlePlaceSelect = useCallback((place: PlaceDetails) => {
    if (place && place.location) {
      if (!checkLocation.isLocationSupported(place.location.lat, place.location.lng)) {
        handleOutOfBoundsToast();
        reset(defaultValues);
        return;
      }
      setSearchInputValue(place.displayName || place.formattedAddress || "");
      setMapCenter(place.location);
      setHasUserSelectedLocation(true);
      setShowForm(true);
      if (place.addressComponents) {
        // Use the utility to parse address components
        const parsed = parseAddressComponents(place.addressComponents);

        // Normalize city using checkLocation
        const normalizedCity = parsed.city ? checkLocation.findCityIdByName(parsed.city) || "bengaluru" : "bengaluru";

        // Determine if the place name is a project (different from locality)
        const isProject = place.displayName ? isProjectName(place.displayName, place.addressComponents) : false;
        setIsDisabled({
          projectName: isProject,
          "location.locality": Boolean(parsed.locality),
          "location.pin": Boolean(parsed.postalCode)
        });
        updateFormFields({
          "location.coordinates": place.location,
          "location.address": place.formattedAddress || "",
          projectName: place.displayName || "",
          projectId: "",
          flatNumber: "",
          blockName: "",
          "location.locality": parsed.locality,
          "location.city": normalizedCity,
          "location.pin": parsed.postalCode
        });
        setAddressData(place.formattedAddress || "");
      }
    }
  }, [handleOutOfBoundsToast, reset, defaultValues, updateFormFields]);
  const handleMapClick = useCallback(async (event: MapMouseEvent) => {
    if (event.detail.latLng) {
      const position = {
        lat: event.detail.latLng.lat,
        lng: event.detail.latLng.lng
      };
      if (!checkLocation.isLocationSupported(position.lat, position.lng)) {
        handleOutOfBoundsToast();
        reset(defaultValues);
        return;
      }
      setHasUserSelectedLocation(true);
      setJustClicked(true);
      setShowForm(true);
      setMapCenter(position);
      setValue("location.coordinates", position, {
        shouldDirty: true
      });
      setIsLoadingLocation(true);
      try {
        const result = await geocodeLocation(position);
        updateFormFields({
          "location.address": result.formattedAddress,
          projectName: result.projectName,
          projectId: "",
          flatNumber: "",
          blockName: "",
          "location.locality": result.locality,
          "location.city": result.city,
          "location.pin": result.postalCode
        });
        setIsDisabled({
          "location.locality": Boolean(result.locality),
          "location.pin": Boolean(result.postalCode)
        });
        setSearchInputValue(result.projectName);
        setAddressData(result.formattedAddress);
      } catch (_error) {
        toast.error("Unable to get address details", {
          description: "Please try again or enter the address manually below."
        });
      } finally {
        setIsLoadingLocation(false);
        scheduleJustClickedReset();
      }
    }
  }, [setValue, handleOutOfBoundsToast, reset, defaultValues, scheduleJustClickedReset, updateFormFields]);
  const handleMarkerDrag = useCallback(async (event: google.maps.MapMouseEvent, isDragEnd: boolean) => {
    if (!isDragEnd) return;
    if (event.latLng) {
      const position = {
        lat: event.latLng.lat(),
        lng: event.latLng.lng()
      };
      if (!checkLocation.isLocationSupported(position.lat, position.lng)) {
        handleOutOfBoundsToast();
        reset(defaultValues);
        setIsLoadingLocation(false);
        return;
      }
      setMapCenter(position);
      setValue("location.coordinates", position, {
        shouldDirty: true
      });
      setIsLoadingLocation(true);
      try {
        const result = await geocodeLocation(position);
        updateFormFields({
          "location.address": result.formattedAddress,
          projectName: result.projectName,
          projectId: "",
          flatNumber: "",
          blockName: "",
          "location.locality": result.locality,
          "location.city": result.city,
          "location.pin": result.postalCode
        });
        setIsDisabled({});
        setIsLoadingLocation(false);
      } catch (_error) {
        toast.error("Unable to get address details", {
          description: "Please try again or enter the address manually below."
        });
        setIsLoadingLocation(false);
      }
    }
  }, [setValue, handleOutOfBoundsToast, reset, defaultValues, updateFormFields]);
  const handleMapDragEnd = useCallback(async (e: MapEvent) => {
    if (isLoadingLocation || justClicked) return;
    if (!e.map?.getCenter) return;
    const center = e.map.getCenter();
    if (!center) return;
    const newCenter = {
      lat: center.lat(),
      lng: center.lng()
    };
    if (!checkLocation.isLocationSupported(newCenter.lat, newCenter.lng)) {
      logger.warn("Map drag end: Coordinates out of service bounds", {
        lat: Number(newCenter.lat.toFixed(3)),
        lng: Number(newCenter.lng.toFixed(3))
      });
      reset(defaultValues);
      handleOutOfBoundsToast();
      setCenterActivated(false);
      return;
    }
    setMapCenter(newCenter);
    setCenterActivated(false);
    setHasUserSelectedLocation(true);
    setShowForm(true);
    setIsDisabled({});
    setIsLoadingLocation(true);
    try {
      const result = await geocodeLocation(newCenter);
      updateFormFields({
        "location.address": result.formattedAddress,
        "location.locality": result.locality,
        "location.city": result.city,
        "location.pin": result.postalCode,
        projectName: result.projectName,
        projectId: "",
        flatNumber: "",
        blockName: "",
        "location.coordinates": newCenter
      });
      setAddressData(result.formattedAddress);
      setIsLoadingLocation(false);
    } catch (_error) {
      toast.error("Unable to get address details", {
        description: "Please try again or enter the address manually below."
      });
      setIsLoadingLocation(false);
    }
  }, [isLoadingLocation, justClicked, reset, defaultValues, handleOutOfBoundsToast, updateFormFields]);
  const handleCurrentLocation = useCallback(async () => {
    if (navigator.geolocation) {
      setIsLoadingLocation(true);
      navigator.geolocation.getCurrentPosition(async position => {
        if (!isMountedRef.current) {
          setIsLoadingLocation(false);
          return;
        }
        const pos = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        if (!checkLocation.isLocationSupported(pos.lat, pos.lng)) {
          handleOutOfBoundsToast();
          if (isMountedRef.current) setIsLoadingLocation(false);
          return;
        }
        setMapCenter(pos);
        setValue("location.coordinates", pos, {
          shouldDirty: true
        });
        setHasUserSelectedLocation(true);
        setShowForm(true);
        setIsDisabled({});
        try {
          const result = await geocodeLocation(pos);
          if (!isMountedRef.current) return;
          updateFormFields({
            "location.address": result.formattedAddress,
            projectName: result.projectName,
            "location.locality": result.locality,
            projectId: "",
            flatNumber: "",
            blockName: "",
            "location.city": result.city,
            "location.pin": result.postalCode
          });
          setAddressData(result.formattedAddress);
        } catch (error) {
          logger.error("Error in handleCurrentLocation geocoding:", error);
          toast.error("Unable to get address details", {
            description: "Please try again or enter the address manually below."
          });
        } finally {
          if (isMountedRef.current) setIsLoadingLocation(false);
        }
      }, _error => {
        toast.error("Unable to access your location", {
          description: "Please allow location access and try again."
        });
        if (isMountedRef.current) setIsLoadingLocation(false);
      });
    } else {
      toast.error("Geolocation not supported", {
        description: "Your browser doesn't support location services."
      });
    }
  }, [setValue, handleOutOfBoundsToast, updateFormFields]);
  const handleProjectClick = useCallback((project: NearbyProject) => {
    const coords = {
      lat: project.location.gps.coordinates[1],
      lng: project.location.gps.coordinates[0]
    };
    if (!checkLocation.isLocationSupported(coords.lat, coords.lng)) {
      handleOutOfBoundsToast();
      reset(defaultValues);
      return;
    }
    setSearchInputValue(project.name);
    setMapCenter(coords);
    setShowForm(true);
    setIsDisabled({
      "location.coordinates": Boolean(coords),
      "location.locality": Boolean(project.localityName),
      "location.pin": Boolean(project.location.pin),
      projectName: Boolean(project.name)
    });
    setHasUserSelectedLocation(true);
    updateFormFields({
      "location.coordinates": coords,
      "location.address": project.location.address,
      "location.locality": project.localityName,
      "location.city": project.location.city,
      "location.pin": project.location.pin,
      projectName: project.name,
      projectId: project.id,
      flatNumber: "",
      blockName: ""
    });
  }, [handleOutOfBoundsToast, reset, defaultValues, updateFormFields]);
  const handleMapDrag = useCallback((_: MapEvent) => {
    setCenterActivated(true);
  }, []);
  const onSubmit = useCallback((data: AddressFormData) => {
    if (!hasUserSelectedLocation) {
      toast.error("Please select a location", {
        description: "Tap on the map or search to choose your property location."
      });
      return;
    }
    onNext({
      ...data,
      location: {
        ...data.location,
        coordinates: mapCenter
      }
    });
  }, [hasUserSelectedLocation, onNext, mapCenter]);
  const canProceed = useMemo(() => Boolean(hasUserSelectedLocation && memoizedWatchedValues.projectName && memoizedWatchedValues.location.locality), [hasUserSelectedLocation, memoizedWatchedValues.projectName, memoizedWatchedValues.location.locality]);
  const handleZoom = useCallback((camera: MapCameraChangedEvent) => {
    setMapZoom(camera.detail.zoom);
  }, []);

  // Memoize return object to prevent unnecessary re-renders in consuming components
  return useMemo(() => ({
    // Form
    control,
    handleSubmit,
    setValue,
    reset,
    watchedValues: memoizedWatchedValues,
    propertyType,
    isCommunity,
    onSubmit,
    canProceed,
    // State
    mapCenter,
    setMapCenter,
    nearbyProjects,
    showForm,
    setShowForm,
    searchInputValue,
    setSearchInputValue,
    isLoadingLocation,
    centerActivated,
    addressData,
    hasUserSelectedLocation,
    isHovered,
    setIsHovered,
    // Handlers
    handlePlaceSelect,
    handleMapClick,
    handleMarkerDrag,
    handleMapDragEnd,
    handleCurrentLocation,
    handleProjectClick,
    handleMapDrag,
    handleConfirmLocation: () => setShowForm(true),
    handleCloseForm: () => setShowForm(false),
    isDisabled,
    handleZoom,
    mapZoom,
    setMapZoom
  }), [control, handleSubmit, setValue, reset, memoizedWatchedValues, propertyType, isCommunity, onSubmit, canProceed, mapCenter, nearbyProjects, showForm, searchInputValue, isLoadingLocation, centerActivated, addressData, hasUserSelectedLocation, isHovered, handlePlaceSelect, handleMapClick, handleMarkerDrag, handleMapDragEnd, handleCurrentLocation, handleProjectClick, handleMapDrag, isDisabled, handleZoom, mapZoom]);
}